import { isElectron } from "../util/ElectronDetection";

/**
 * Local Notification Service for Electron
 *
 * Handles scheduling and management of local notifications when running in Electron.
 * This service works independently of Firebase Cloud Messaging and provides
 * reliable notifications when the app is running.
 *
 * Uses a polling-based approach to check stored notification times periodically
 * instead of relying on multiple setTimeout calls.
 *
 * IMPORTANT: This service ONLY works in Electron mode. It will not function in browser mode.
 */
class LocalNotificationService {
  constructor() {
    this.pendingNotifications = new Map(); // Store notification data with scheduled times
    this.shownNotifications = new Set(); // Track notifications that have already been shown
    this.shownNotificationsWithTimestamp = new Map(); // Track shown notifications with timestamps for better persistence
    this.pollingInterval = null; // Polling timer for checking notifications
    this.syncInterval = null;
    this.lastSyncTime = null;
    this.isInitialized = false;
    this.currentScheduleHash = null; // To detect changes
    this.syncDebounceTimeout = null; // To prevent rapid successive syncs
    this.isManualSync = false; // Track if current sync is manual
    this.isProcessing = false; // Prevent concurrent processing
    this.POLLING_INTERVAL_MS = 30000; // Check every 30 seconds
    this.STORAGE_FILE = "local-notifications-shown.json"; // Persistent storage file
    this.saveDebounceTimeout = null; // Debounce saving to disk
    this.ipcCleanupFunction = null; // Store IPC listener cleanup function
    this.hookInstanceCount = 0; // Track how many hook instances are using this service

    // Only initialize if we're in Electron - this service does not work in browser mode
    const electronDetected = isElectron();
    if (electronDetected) {
      this.initialize();
    }
  }

  /**
   * Initialize the service
   */
  async initialize() {
    if (this.isInitialized || !isElectron()) {
      return;
    }

    this.isInitialized = true;

    // Load previously shown notifications from persistent storage
    await this.loadShownNotificationsFromStorage();

    // Start the polling mechanism
    this.startPolling();

    // Listen for navigation events from notifications
    if (window.electronAPI && window.electronAPI.localNotifications) {
      // Clean up any existing listener first
      if (this.ipcCleanupFunction) {
        this.ipcCleanupFunction();
        this.ipcCleanupFunction = null;
      }

      // Set up new listener and store cleanup function
      this.ipcCleanupFunction =
        window.electronAPI.localNotifications.onNavigateToVideo((data) => {
          this.handleNotificationClick(data);
        });
    }
  }

  /**
   * Schedule notifications from API response
   * @param {Array} notifications - Array of notification objects from API
   * @param {boolean} skipOverdueNotifications - Whether to skip showing overdue notifications (for manual operations)
   */
  async scheduleNotifications(notifications, skipOverdueNotifications = false) {
    // Only work in Electron mode
    if (!isElectron()) {
      return;
    }

    if (!notifications || !Array.isArray(notifications)) {
      return;
    }

    // Prevent concurrent processing
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    // Ensure we're properly initialized and loaded
    if (!this.isInitialized) {
      await this.initialize();
    }

    // Clear pending notifications but preserve shown tracking
    this.pendingNotifications.clear();

    // Process notifications and store them for polling
    const now = new Date();
    let futureCount = 0;
    let overdueCount = 0;
    let ignoredCount = 0;
    let duplicateCount = 0;
    const seenNotifications = new Set(); // Track duplicates by content

    notifications.forEach((notification) => {
      if (notification.schedules && Array.isArray(notification.schedules)) {
        notification.schedules.forEach((schedule) => {
          if (
            schedule.notificationTime &&
            this.isValidFutureTime(schedule.notificationTime)
          ) {
            const notificationData = {
              scheduleId: schedule.id,
              notificationId: notification.id,
              title: notification.name,
              body: notification.description,
              time: new Date(schedule.notificationTime),
              videoId: schedule.video?.id || notification.video?.id || null,
              preferenceId: notification.preferenceId,
              type: notification.type,
            };

            // Skip if already shown (improved checking)
            if (
              this.isNotificationAlreadyShown(schedule.id, notificationData)
            ) {
              // Also remove from pending notifications if it exists there
              this.pendingNotifications.delete(schedule.id);
              return;
            }

            // Create a content-based key for duplicate detection
            const contentKey = `${notification.type}-${notification.preferenceId}-${schedule.notificationTime}`;

            // Check for content duplicates
            if (seenNotifications.has(contentKey)) {
              duplicateCount++;
              return;
            }
            seenNotifications.add(contentKey);

            const delay = notificationData.time.getTime() - now.getTime();

            if (delay >= -10 * 60 * 1000) {
              // Include notifications up to 10 minutes overdue
              if (delay >= 0) {
                futureCount++;
              } else {
                overdueCount++;
              }

              // Store notification for polling system
              this.pendingNotifications.set(schedule.id, notificationData);
            } else {
              // Ignore notifications older than 10 minutes
              ignoredCount++;
            }
          }
        });
      }
    });

    // Handle overdue notifications immediately if not skipped
    if (!skipOverdueNotifications) {
      this.checkAndShowOverdueNotifications();
    }

    // Update the schedule hash for change detection
    this.currentScheduleHash = this.generateScheduleHash(notifications);

    // Release the processing lock
    this.isProcessing = false;
  }

  /**
   * Start the polling mechanism to check for due notifications
   */
  startPolling() {
    if (this.pollingInterval) {
      return; // Already polling
    }

    this.pollingInterval = setInterval(() => {
      this.checkForDueNotifications();
    }, this.POLLING_INTERVAL_MS);

    // Also check immediately
    this.checkForDueNotifications();
  }

  /**
   * Stop the polling mechanism
   */
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * Check for notifications that are due to be shown
   */
  checkForDueNotifications() {
    if (this.pendingNotifications.size === 0) {
      return;
    }

    const now = new Date();
    const dueNotifications = [];

    // Check each pending notification
    for (const [scheduleId, notificationData] of this.pendingNotifications) {
      // Skip if already shown (improved checking)
      if (this.isNotificationAlreadyShown(scheduleId, notificationData)) {
        continue;
      }

      const timeDiff = notificationData.time.getTime() - now.getTime();

      // Show notification if it's due (within 30 seconds of scheduled time or overdue)
      if (timeDiff <= 30000) {
        // 30 seconds tolerance
        dueNotifications.push({ scheduleId, notificationData });
      }
    }

    // Show due notifications
    dueNotifications.forEach(({ scheduleId, notificationData }) => {
      this.showNotification(notificationData);
      this.markNotificationAsShown(scheduleId, notificationData);
      this.pendingNotifications.delete(scheduleId);
    });

    // Clean up old shown notifications periodically (every 10 polling cycles = ~5 minutes)
    if (!this.cleanupCounter) this.cleanupCounter = 0;
    this.cleanupCounter++;
    if (this.cleanupCounter >= 10) {
      this.cleanupOldShownNotifications();
      this.cleanupCounter = 0;
    }
  }

  /**
   * Clean up old shown notifications to prevent memory growth
   * Removes shown notification IDs that are more than 24 hours old
   */
  cleanupOldShownNotifications() {
    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    let cleanedCount = 0;

    // Clean up notifications older than 24 hours
    for (const [scheduleId, shownData] of this
      .shownNotificationsWithTimestamp) {
      if (shownData.shownAt < twentyFourHoursAgo) {
        this.shownNotificationsWithTimestamp.delete(scheduleId);
        this.shownNotifications.delete(scheduleId);
        cleanedCount++;
      }
    }

    // Also limit total size as a safety measure
    if (this.shownNotifications.size > 1000) {
      // Sort by timestamp and keep the most recent 500
      const sortedEntries = Array.from(
        this.shownNotificationsWithTimestamp.entries(),
      )
        .sort((a, b) => b[1].shownAt.getTime() - a[1].shownAt.getTime())
        .slice(0, 500);

      this.shownNotifications.clear();
      this.shownNotificationsWithTimestamp.clear();

      sortedEntries.forEach(([scheduleId, shownData]) => {
        this.shownNotifications.add(scheduleId);
        this.shownNotificationsWithTimestamp.set(scheduleId, shownData);
      });
    }

    if (cleanedCount > 0) {
      // Save to storage after cleanup
      this.saveShownNotificationsToStorage();
    }
  }

  /**
   * Load shown notifications from persistent storage (Electron only)
   */
  async loadShownNotificationsFromStorage() {
    if (!isElectron()) {
      return;
    }

    if (!window.electronAPI || !window.electronAPI.storage) {
      return;
    }

    try {
      const storedData = await window.electronAPI.storage.get(
        this.STORAGE_FILE,
      );

      if (storedData && storedData.shownNotifications) {
        const now = new Date();
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        let loadedCount = 0;

        // Load shown notifications, filtering out old ones
        for (const [scheduleId, data] of Object.entries(
          storedData.shownNotifications,
        )) {
          const shownAt = new Date(data.shownAt);
          const scheduledTime = new Date(data.scheduledTime);

          // Only load notifications from the last 7 days
          if (shownAt > sevenDaysAgo) {
            this.shownNotifications.add(scheduleId);
            this.shownNotificationsWithTimestamp.set(scheduleId, {
              shownAt,
              scheduledTime,
              title: data.title,
              type: data.type,
              preferenceId: data.preferenceId,
            });
            loadedCount++;
          }
        }
      }
    } catch (error) {
      console.error(
        "[LocalNotificationService] Error loading shown notifications from Electron storage:",
        error,
      );
    }
  }

  /**
   * Save shown notifications to persistent storage (Electron only, debounced)
   */
  saveShownNotificationsToStorage() {
    if (!isElectron()) {
      return;
    }

    if (!window.electronAPI || !window.electronAPI.storage) {
      return;
    }

    // Debounce saves to avoid excessive disk writes
    if (this.saveDebounceTimeout) {
      clearTimeout(this.saveDebounceTimeout);
    }

    this.saveDebounceTimeout = setTimeout(async () => {
      try {
        // Convert Map to plain object for JSON serialization
        const dataToSave = {
          shownNotifications: Object.fromEntries(
            Array.from(this.shownNotificationsWithTimestamp.entries()).map(
              ([id, data]) => [
                id,
                {
                  shownAt: data.shownAt.toISOString(),
                  scheduledTime: data.scheduledTime.toISOString(),
                  title: data.title,
                  type: data.type,
                  preferenceId: data.preferenceId,
                },
              ],
            ),
          ),
          lastUpdated: new Date().toISOString(),
          version: "1.0",
        };

        await window.electronAPI.storage.set(this.STORAGE_FILE, dataToSave);
      } catch (error) {
        console.error(
          "[LocalNotificationService] Error saving shown notifications to Electron storage:",
          error,
        );
      }
    }, 2000); // 2-second debounce
  }

  /**
   * Mark a notification as shown with timestamp tracking
   * @param {string} scheduleId - The schedule ID
   * @param {Object} notificationData - The notification data
   */
  markNotificationAsShown(scheduleId, notificationData) {
    const now = new Date();
    this.shownNotifications.add(scheduleId);
    this.shownNotificationsWithTimestamp.set(scheduleId, {
      shownAt: now,
      scheduledTime: notificationData.time,
      title: notificationData.title,
      type: notificationData.type,
      preferenceId: notificationData.preferenceId,
    });

    // Save to persistent storage
    this.saveShownNotificationsToStorage();
  }

  /**
   * Check if a notification has already been shown
   * @param {string} scheduleId - The schedule ID
   * @param {Object} notificationData - The notification data
   * @returns {boolean} True if already shown
   */
  isNotificationAlreadyShown(scheduleId, notificationData) {
    // Check by schedule ID first
    if (this.shownNotifications.has(scheduleId)) {
      return true;
    }

    // Check by content and time (for cases where schedule ID might change but content is the same)
    const contentKey = `${notificationData.type}-${
      notificationData.preferenceId
    }-${notificationData.time.getTime()}`;

    for (const [shownId, shownData] of this.shownNotificationsWithTimestamp) {
      const shownContentKey = `${shownData.type}-${
        shownData.preferenceId
      }-${shownData.scheduledTime.getTime()}`;
      if (contentKey === shownContentKey) {
        // Also add this schedule ID to the shown set for future quick lookups
        this.shownNotifications.add(scheduleId);
        return true;
      }
    }

    return false;
  }

  /**
   * Check and show the most recent overdue notification
   */
  checkAndShowOverdueNotifications() {
    const now = new Date();
    const overdueNotifications = [];

    // Find overdue notifications that haven't been shown yet
    for (const [scheduleId, notificationData] of this.pendingNotifications) {
      // Skip if already shown
      if (this.isNotificationAlreadyShown(scheduleId, notificationData)) {
        continue;
      }

      const timeDiff = notificationData.time.getTime() - now.getTime();

      if (timeDiff < 0 && timeDiff >= -10 * 60 * 1000) {
        // Overdue but within 10 minutes
        overdueNotifications.push({ scheduleId, notificationData, timeDiff });
      }
    }

    if (overdueNotifications.length > 0) {
      // Sort by most recent (least negative timeDiff)
      overdueNotifications.sort((a, b) => b.timeDiff - a.timeDiff);
      const mostRecent = overdueNotifications[0];

      // Show after a short delay
      setTimeout(() => {
        this.showNotification(mostRecent.notificationData);
        this.markNotificationAsShown(
          mostRecent.scheduleId,
          mostRecent.notificationData,
        );
        this.pendingNotifications.delete(mostRecent.scheduleId);
      }, 2000);
    }
  }

  /**
   * Show a notification using Electron's notification system (Electron only)
   * @param {Object} data - Notification data
   */
  async showNotification(data) {
    if (!isElectron()) {
      return;
    }

    if (!window.electronAPI || !window.electronAPI.localNotifications) {
      console.warn(
        "[LocalNotificationService] Cannot show notification - Electron API unavailable",
      );
      return;
    }

    try {
      await window.electronAPI.localNotifications.show({
        title: data.title,
        body: data.body,
        videoId: data.videoId,
        preferenceId: data.preferenceId,
        scheduleId: data.scheduleId,
        notificationId: data.notificationId,
      });
    } catch (error) {
      console.error(
        "[LocalNotificationService] Error showing notification:",
        error,
      );
    }
  }

  /**
   * Handle notification click events
   * @param {Object} data - Notification click data
   */
  handleNotificationClick(data) {
    const videoId = data.videoId || null;
    let videoUrl;

    if (videoId && videoId !== "null") {
      videoUrl = `/home/library?videoId=${videoId}`;
    } else {
      videoUrl = `/home/library?preferenceName=${encodeURIComponent(data.title || "")}`;
    }

    if (window.electronAPI && window.electronAPI.focusWindow) {
      window.electronAPI.focusWindow();
    }

    window.dispatchEvent(
      new CustomEvent("local-notification-navigate", {
        detail: {
          url: videoUrl,
          videoId: data.videoId,
          preferenceId: data.preferenceId,
          preferenceName: data.title,
          scheduleId: data.scheduleId,
          notificationId: data.notificationId,
        },
      }),
    );
  }

  /**
   * Start periodic sync with server
   * @param {Function} apiCall - Function to call the API for schedule data
   * @param {boolean} immediateSync - Whether to perform an immediate sync
   */
  startPeriodicSync(apiCall, immediateSync = false) {
    if (!isElectron() || this.syncInterval) {
      return;
    }

    // Store the API call function for manual syncs
    this.apiCallFunction = apiCall;

    // Perform immediate sync if requested (useful for auto-login scenarios)
    if (immediateSync) {
      setTimeout(async () => {
        try {
          await this.syncWithServer(apiCall);
        } catch (error) {
          console.error(
            "[LocalNotificationService] Immediate sync error:",
            error,
          );
        }
      }, 2000); // Small delay to ensure everything is initialized
    }

    // Sync every 30 minutes
    this.syncInterval = setInterval(
      async () => {
        try {
          await this.syncWithServer(apiCall);
        } catch (error) {
          console.error("[LocalNotificationService] Sync error:", error);
        }
      },
      30 * 60 * 1000,
    ); // 30 minutes
  }

  /**
   * Trigger an immediate sync (for when notifications are added/modified)
   * Uses debouncing to prevent multiple rapid syncs
   * @returns {Promise<void>}
   */
  async triggerImmediateSync() {
    if (!isElectron() || !this.apiCallFunction) {
      return;
    }

    // Clear any existing debounce timeout
    if (this.syncDebounceTimeout) {
      clearTimeout(this.syncDebounceTimeout);
    }

    // Debounce the sync to prevent rapid successive calls
    this.syncDebounceTimeout = setTimeout(async () => {
      try {
        this.isManualSync = true;
        await this.syncWithServer(this.apiCallFunction);
        this.isManualSync = false;
        this.syncDebounceTimeout = null;
      } catch (error) {
        console.error(
          "[LocalNotificationService] Immediate sync failed:",
          error,
        );
        this.isManualSync = false;
        this.syncDebounceTimeout = null;
      }
    }, 1000); // 1 second debounce
  }

  /**
   * Sync with server to update notifications
   * @param {Function} apiCall - Function to call the API for schedule data
   */
  async syncWithServer(apiCall) {
    if (!isElectron() || typeof apiCall !== "function") {
      return;
    }

    try {
      const scheduleData = await apiCall();
      const newScheduleHash = this.generateScheduleHash(
        scheduleData.notifications || [],
      );

      // Only update if there are changes
      if (newScheduleHash !== this.currentScheduleHash) {
        await this.scheduleNotifications(
          scheduleData.notifications || [],
          this.isManualSync,
        );
      }

      this.lastSyncTime = new Date();
    } catch (error) {
      console.error(
        "[LocalNotificationService] Failed to sync schedule:",
        error,
      );
    }
  }

  /**
   * Generate a hash of the schedule for change detection
   * @param {Array} notifications - Notifications array
   * @returns {string} Hash string
   */
  generateScheduleHash(notifications) {
    if (!Array.isArray(notifications)) return "";

    const scheduleData = notifications.map((n) => ({
      id: n.id,
      schedules: (n.schedules || []).map((s) => ({
        id: s.id,
        notificationTime: s.notificationTime,
      })),
    }));

    return JSON.stringify(scheduleData);
  }

  /**
   * Check if a time is valid and not too old
   * @param {string} timeString - ISO time string
   * @returns {boolean} True if valid time (future or recent past)
   */
  isValidFutureTime(timeString) {
    try {
      const time = new Date(timeString);
      const now = new Date();
      const timeDiff = time.getTime() - now.getTime();

      // Accept notifications that are in the future or up to 24 hours overdue
      return timeDiff > -24 * 60 * 60 * 1000;
    } catch {
      return false;
    }
  }

  /**
   * Clear all pending notifications (Electron only)
   */
  async clearAllNotifications() {
    if (!isElectron()) {
      return;
    }

    // Clear sync debounce timeout if it exists
    if (this.syncDebounceTimeout) {
      clearTimeout(this.syncDebounceTimeout);
      this.syncDebounceTimeout = null;
    }

    // Clear all pending notifications and shown tracking
    this.pendingNotifications.clear();
    this.shownNotifications.clear();
    this.shownNotificationsWithTimestamp.clear();

    // Clear persistent storage
    if (window.electronAPI && window.electronAPI.storage) {
      try {
        await window.electronAPI.storage.delete(this.STORAGE_FILE);
      } catch (error) {
        console.error(
          "[LocalNotificationService] Error clearing Electron persistent storage:",
          error,
        );
      }
    }
  }

  /**
   * Clear only pending notifications (preserve shown tracking)
   */
  clearPendingNotifications() {
    this.pendingNotifications.clear();
  }

  /**
   * Get info about currently pending notifications
   * @returns {Object} Status information
   */
  getStatus() {
    return {
      isElectron: isElectron(),
      isEnabled: isElectron(), // Service only works in Electron
      isInitialized: this.isInitialized,
      pendingCount: this.pendingNotifications.size,
      shownCount: this.shownNotifications.size,
      lastSyncTime: this.lastSyncTime,
      syncIntervalActive: !!this.syncInterval,
      pollingActive: !!this.pollingInterval,
      pollingIntervalMs: this.POLLING_INTERVAL_MS,
      storageType: isElectron() ? "electron" : "disabled",
    };
  }

  /**
   * Get detailed info about pending notifications for debugging
   * @returns {Object} Detailed status information
   */
  getDetailedStatus() {
    const now = new Date();
    const pendingDetails = [];

    for (const [scheduleId, notificationData] of this.pendingNotifications) {
      const timeDiff = notificationData.time.getTime() - now.getTime();
      const minutesUntilDue = Math.round(timeDiff / 1000 / 60);

      pendingDetails.push({
        scheduleId,
        title: notificationData.title,
        type: notificationData.type,
        preferenceId: notificationData.preferenceId,
        scheduledTime: notificationData.time.toLocaleString(),
        minutesUntilDue,
        isOverdue: timeDiff < 0,
      });
    }

    // Sort by time until due
    pendingDetails.sort((a, b) => a.minutesUntilDue - b.minutesUntilDue);

    // Group by type and time to show potential duplicates
    const duplicateCheck = {};
    pendingDetails.forEach((notification) => {
      const key = `${notification.type}-${notification.preferenceId}-${notification.scheduledTime}`;
      if (!duplicateCheck[key]) {
        duplicateCheck[key] = [];
      }
      duplicateCheck[key].push(notification.scheduleId);
    });

    const potentialDuplicates = Object.entries(duplicateCheck)
      .filter(([key, ids]) => ids.length > 1)
      .map(([key, ids]) => ({ key, scheduleIds: ids }));

    // Get shown notifications with details
    const shownDetails = Array.from(
      this.shownNotificationsWithTimestamp.entries(),
    ).map(([scheduleId, data]) => ({
      scheduleId,
      title: data.title,
      shownAt: data.shownAt.toLocaleString(),
      scheduledTime: data.scheduledTime.toLocaleString(),
      type: data.type,
      preferenceId: data.preferenceId,
    }));

    return {
      ...this.getStatus(),
      currentTime: now.toLocaleString(),
      pendingNotifications: pendingDetails,
      shownNotificationIds: Array.from(this.shownNotifications),
      shownNotificationsDetails: shownDetails,
      potentialDuplicates,
      duplicateCheckSummary: `${potentialDuplicates.length} potential duplicate groups found`,
      trackingInfo: {
        shownNotificationsCount: this.shownNotifications.size,
        shownWithTimestampCount: this.shownNotificationsWithTimestamp.size,
        oldestShown:
          shownDetails.length > 0
            ? shownDetails.reduce((oldest, current) =>
                new Date(current.shownAt) < new Date(oldest.shownAt)
                  ? current
                  : oldest,
              ).shownAt
            : "None",
      },
      // Note: Storage info requires async call, use getStorageInfo() separately
      storageFile: this.STORAGE_FILE,
    };
  }

  /**
   * Get storage information for debugging
   * @returns {Object} Storage information
   */
  async getStorageInfo() {
    if (!isElectron()) {
      // Browser storage fallback
      try {
        const stored = localStorage.getItem(this.STORAGE_FILE);
        const storedData = stored ? JSON.parse(stored) : null;
        return {
          available: true,
          storageType: "browser-localStorage",
          hasStoredData: !!storedData,
          storedCount: storedData?.shownNotifications
            ? Object.keys(storedData.shownNotifications).length
            : 0,
          lastUpdated: storedData?.lastUpdated || "Never",
          version: storedData?.version || "Unknown",
          filePath: `localStorage:${this.STORAGE_FILE}`,
        };
      } catch (error) {
        return {
          available: false,
          storageType: "browser-localStorage",
          error: error.message,
        };
      }
    }

    if (!window.electronAPI || !window.electronAPI.storage) {
      return {
        available: false,
        storageType: "electron",
        error: "Storage API not available",
      };
    }

    try {
      const storedData = await window.electronAPI.storage.get(
        this.STORAGE_FILE,
      );
      return {
        available: true,
        storageType: "electron",
        hasStoredData: !!storedData,
        storedCount: storedData?.shownNotifications
          ? Object.keys(storedData.shownNotifications).length
          : 0,
        lastUpdated: storedData?.lastUpdated || "Never",
        version: storedData?.version || "Unknown",
        filePath: this.STORAGE_FILE,
      };
    } catch (error) {
      return {
        available: true,
        storageType: "electron",
        hasStoredData: false,
        error: error.message,
      };
    }
  }

  /**
   * Get a summary of shown notifications for debugging
   * @returns {Object} Summary of shown notifications
   */
  getShownNotificationsSummary() {
    const now = new Date();
    const summary = {
      totalShown: this.shownNotifications.size,
      recentlyShown: 0,
      todayShown: 0,
      byType: {},
      byPreference: {},
    };

    const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

    for (const [scheduleId, data] of this.shownNotificationsWithTimestamp) {
      // Count recent (last hour)
      if (data.shownAt > oneHourAgo) {
        summary.recentlyShown++;
      }

      // Count today
      if (data.shownAt > todayStart) {
        summary.todayShown++;
      }

      // Count by type
      summary.byType[data.type] = (summary.byType[data.type] || 0) + 1;

      // Count by preference
      summary.byPreference[data.preferenceId] =
        (summary.byPreference[data.preferenceId] || 0) + 1;
    }

    return summary;
  }

  /**
   * Register a hook instance using this service
   */
  registerHookInstance() {
    this.hookInstanceCount++;
  }

  /**
   * Unregister a hook instance
   */
  unregisterHookInstance() {
    this.hookInstanceCount = Math.max(0, this.hookInstanceCount - 1);

    // Only cleanup if no more hook instances are using the service
    if (this.hookInstanceCount === 0) {
      this.performCleanup();
    }
  }

  /**
   * Stop sync and cleanup (preserves shown notifications)
   */
  cleanup() {
    this.performCleanup();
  }

  /**
   * Internal cleanup method
   */
  performCleanup() {
    // Only clear pending notifications, NOT shown notifications
    this.clearPendingNotifications();
    this.stopPolling();

    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }

    // Clear debounce timeouts
    if (this.syncDebounceTimeout) {
      clearTimeout(this.syncDebounceTimeout);
      this.syncDebounceTimeout = null;
    }

    if (this.saveDebounceTimeout) {
      clearTimeout(this.saveDebounceTimeout);
      this.saveDebounceTimeout = null;
    }

    // Clean up IPC listener
    if (this.ipcCleanupFunction) {
      this.ipcCleanupFunction();
      this.ipcCleanupFunction = null;
    }

    this.apiCallFunction = null;
    this.isInitialized = false;
  }
}

// Create and export a singleton instance
const localNotificationService = new LocalNotificationService();

// Expose service on window for debugging (development only)
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  window.localNotificationService = localNotificationService;
}

export default localNotificationService;
